import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Eye, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { parseDateAsLocal } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import { useLanguage } from "@/contexts/LanguageContext";

export const MemberDirectory = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [payments, setPayments] = useState<any[]>([]);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const { data: membersData, error } = await supabase
        .from("members")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Load family members for each member
      const membersWithFamily = await Promise.all(
        (membersData || []).map(async (member) => {
          const { data: familyData } = await supabase
            .from("family_members")
            .select("*")
            .eq("member_id", member.id);
          
          return {
            ...member,
            family_members: familyData || []
          };
        })
      );

      setMembers(membersWithFamily);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to load members",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth: string | null) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = parseDateAsLocal(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatMonthDay = (dateOfBirth: string | null) => {
    if (!dateOfBirth) return null;
    const date = parseDateAsLocal(dateOfBirth);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  const loadMemberPayments = async (memberId: string) => {
    try {
      const { data, error } = await supabase
        .from("donations")
        .select("*")
        .eq("member_id", memberId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPayments(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to load payment history",
        variant: "destructive",
      });
    }
  };

  const handleViewMember = async (member: any) => {
    setSelectedMember(member);
    await loadMemberPayments(member.id);
  };

  const handleApprove = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from("members")
        .update({ membership_status: "active" })
        .eq("id", memberId);

      if (error) throw error;

      toast({
        title: t("admin.members.approve.success"),
        description: t("admin.members.approve.success"),
      });

      loadMembers();
    } catch (error: any) {
      toast({
        title: t("member.registration.error"),
        description: t("admin.members.approve.error"),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (memberId: string, memberName: string) => {
    try {
      // First, delete related records to avoid foreign key constraints
      // Delete family relationships
      const { error: familyError } = await supabase
        .from("family_relationships")
        .delete()
        .or(`member_id.eq.${memberId},related_member_id.eq.${memberId}`);

      if (familyError) {
        console.error("Error deleting family relationships:", familyError);
        // Continue anyway - this might not be a critical error
      }

      // Delete the member record
      const { error } = await supabase
        .from("members")
        .delete()
        .eq("id", memberId);

      if (error) throw error;

      toast({
        title: t("member.registration.success"),
        description: t("admin.members.delete.success").replace("{name}", memberName),
      });

      loadMembers();
    } catch (error: any) {
      console.error("Delete error:", error);
      toast({
        title: t("member.registration.error"),
        description: error.message || t("admin.members.delete.error"),
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      active: "default",
      pending: "secondary",
      inactive: "destructive",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("admin.members.directory.title")}</CardTitle>
        <CardDescription>{t("admin.members.directory.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p>{t("admin.members.directory.loading")}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.members.directory.name")}</TableHead>
                <TableHead>{t("admin.members.directory.email")}</TableHead>
                <TableHead>{t("admin.members.directory.phone")}</TableHead>
                <TableHead>{t("admin.members.directory.status")}</TableHead>
                <TableHead>{t("admin.members.directory.children")}</TableHead>
                <TableHead className="w-[100px]">{t("admin.members.directory.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">{member.full_name}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{member.phone || t("admin.members.directory.na")}</TableCell>
                  <TableCell>{getStatusBadge(member.membership_status)}</TableCell>
                  <TableCell>{member.number_of_kids}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewMember(member)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                          <DialogHeader>
                            <DialogTitle>{t("admin.members.details.title")}</DialogTitle>
                          </DialogHeader>
                          <div className="max-h-[60vh] overflow-y-auto pr-2">
                            {selectedMember && (
                              <div className="space-y-6">
                                <div className="space-y-4">
                                  <h4 className="font-semibold">Member Information</h4>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-sm text-muted-foreground">{t("admin.members.directory.name")}</p>
                                      <p className="font-medium">{selectedMember.full_name}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">{t("admin.members.directory.email")}</p>
                                      <p className="font-medium">{selectedMember.email}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">{t("admin.members.directory.phone")}</p>
                                      <p className="font-medium">{selectedMember.phone || t("admin.members.directory.na")}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">{t("admin.members.details.address")}</p>
                                      <p className="font-medium">{selectedMember.address || t("admin.members.directory.na")}</p>
                                    </div>
                                    {selectedMember.date_of_birth && (
                                      <>
                                        <div>
                                          <p className="text-sm text-muted-foreground">Date of Birth</p>
                                          <p className="font-medium">{formatMonthDay(selectedMember.date_of_birth)}</p>
                                        </div>
                                        <div>
                                          <p className="text-sm text-muted-foreground">Age</p>
                                          <p className="font-medium">{calculateAge(selectedMember.date_of_birth)}</p>
                                        </div>
                                      </>
                                    )}
                                    <div>
                                      <p className="text-sm text-muted-foreground">{t("admin.members.directory.children")}</p>
                                      <p className="font-medium">{selectedMember.number_of_kids}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">{t("admin.members.directory.status")}</p>
                                      <p className="font-medium">{getStatusBadge(selectedMember.membership_status)}</p>
                                    </div>
                                  </div>
                                </div>

                                {selectedMember.family_members && selectedMember.family_members.length > 0 && (
                                  <div className="space-y-4">
                                    <h4 className="font-semibold">Family Members</h4>
                                    {selectedMember.family_members.map((familyMember: any) => (
                                      <div key={familyMember.id} className="p-4 border rounded-lg">
                                        <h5 className="font-medium mb-2 capitalize">{familyMember.relationship_type}</h5>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                          <div>
                                            <span className="text-muted-foreground">Name: </span>
                                            <span>{familyMember.full_name}</span>
                                          </div>
                                          {familyMember.phone && (
                                            <div>
                                              <span className="text-muted-foreground">Phone: </span>
                                              <span>{familyMember.phone}</span>
                                            </div>
                                          )}
                                          {familyMember.email && (
                                            <div>
                                              <span className="text-muted-foreground">Email: </span>
                                              <span>{familyMember.email}</span>
                                            </div>
                                          )}
                                          {familyMember.date_of_birth && (
                                            <>
                                              <div>
                                                <span className="text-muted-foreground">Date of Birth: </span>
                                                <span>{formatMonthDay(familyMember.date_of_birth)}</span>
                                              </div>
                                              <div>
                                                <span className="text-muted-foreground">Age: </span>
                                                <span>{calculateAge(familyMember.date_of_birth)}</span>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div>
                                  <h4 className="font-semibold mb-2">{t("admin.members.details.paymenthistory")}</h4>
                                  {payments.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">{t("admin.members.details.nopayments")}</p>
                                  ) : (
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>{t("admin.members.details.date")}</TableHead>
                                          <TableHead>{t("admin.members.details.type")}</TableHead>
                                          <TableHead>{t("admin.members.details.amount")}</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {payments.map((payment) => (
                                          <TableRow key={payment.id}>
                                            <TableCell>
                                              {new Date(payment.created_at).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell className="capitalize">
                                              {payment.payment_type.replace("_", " ")}
                                            </TableCell>
                                            <TableCell>${payment.amount}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                      {member.membership_status === "pending" && (
                        <Button
                          size="sm"
                          onClick={() => handleApprove(member.id)}
                        >
                          {t("admin.members.directory.approve")}
                        </Button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t("admin.members.delete.confirm")}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t("admin.members.delete.description").replace("{name}", member.full_name)}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("admin.members.delete.cancel")}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(member.id, member.full_name)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {t("admin.members.delete.button")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
